import { useEffect, useState } from 'react';
import { useLoading } from '../../context/loadingContext';

interface Props {
  id?: string;
  title?: string;
  type?: `submit` | `reset` | `button`;
  disabled?: boolean;
  color?: `blue` | `red` | `gray` | `green` | `yellow`;
  outline?: boolean;
  textColor?: `white` | `black` | `default`;
  className?: string;
  fullWidth?: boolean;
  icon?: JSX.Element;
  onClick?: (e?: any) => void;
  cleanClass?: boolean;
  showLoading?: boolean;
}

/**
 * Button component
 */
export default function Button({
  id,
  title,
  type = `button`,
  disabled,
  color,
  outline,
  textColor,
  className,
  fullWidth = false,
  icon,
  onClick,
  cleanClass,
  showLoading,
}: Props) {
  const { loading } = useLoading();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const getBgColor = () => {
    if (color === `blue` || color === undefined)
      return outline
        ? `bg-white hover:bg-blue-700 text-blue-700 hover:text-white border-2 border-blue-700`
        : `bg-blue-500 hover:bg-blue-700`;
    if (color === `red`)
      return outline
        ? `bg-white hover:bg-red-700 text-red-700 hover:text-white border-2 border-red-700`
        : `bg-red-500 hover:bg-red-700`;
    if (color === `gray`)
      return outline
        ? `bg-white hover:bg-gray-700 text-gray-700 hover:text-white border-2 border-gray-700`
        : `bg-gray-400 hover:bg-gray-700`;
    if (color === `green`)
      return outline
        ? `bg-white hover:bg-green-500 text-green-500 hover:text-white border border-2 border-green-500`
        : `bg-green-500 hover:bg-green-500`;
    if (color === `yellow`)
      return outline
        ? `bg-white hover:bg-yellow-500 text-yellow-500 hover:text-white border-2 border-yellow-500`
        : `bg-yellow-500 hover:bg-yellow-500`;
  };

  const getTextColor = () => {
    if (textColor === `default`) return ``;
    if (textColor === `white` || textColor === undefined) return `text-white`;
    if (textColor === `black`) return `text-black`;
  };

  const getFullWidth = () => {
    if (fullWidth) return `w-full`;
  };

  /**
   * Handles click event
   * @param e
   */
  const handleOnClick = (e: any) => {
    // if (!onClick) return;
    if (showLoading) setIsLoading(true);
    onClick && onClick(e);
  };

  useEffect(() => {
    if (!showLoading || isLoading) return;
    setIsLoading(false);
  }, [loading]);

  useEffect(() => {
    if (!isLoading) return;
    // if api call did not start within 0.5 second set button loading state back to false
    setTimeout(checkIsLoading, 500);
  }, [isLoading]);

  // To prevent more than one button set to loading state
  const checkIsLoading = () => {
    if (isLoading && !loading) setIsLoading(false);
  };

  const disabledClass = `!bg-gray-400`;
  const defaultClassName = `${getBgColor()} ${getTextColor()} ${getFullWidth()} inline-flex items-center justify-center text-sm font-medium rounded-md
 focus:outline-none px-4 py-2 ${className} ${disabled ? disabledClass : ``}`;

  /**
   * Renders button content
   */
  const btnContent = () => {
    if (showLoading && isLoading && loading)
      return (
        <div className="flex items-center m-0 p-0">
          <svg
            aria-hidden="true"
            role="status"
            className="inline w-4 h-4 mr-1 text-white animate-spin"
            viewBox="0 0 100 101"
            fill="none"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="#E5E7EB"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentColor"
            />
          </svg>
          Loading...
        </div>
      );
    return (
      <>
        {icon && icon}
        {title}
      </>
    );
  };

  return (
    <button
      id={id}
      onClick={(e) => handleOnClick(e)}
      type={type}
      className={cleanClass ? className : defaultClassName}
      disabled={disabled}
    >
      {btnContent()}
    </button>
  );
}
